import { api } from '../../api';
import { EU_LAENDER } from '../laender';
import { istDrittland } from '../laender-welt';

// Gemeinsames für „Ausgaben und Steuer“ (Billtoro Paket 2, Server: modules/rechnungstool-ausgaben): Typen der
// Antworten, Beträge in Cent, Datum deutsch, Dateien laden.

export type Satz = 19 | 7 | 0;
export interface Zeile {
  satz: Satz;
  nettoCents: number;
  steuerCents: number;
}
export type Steuerart = 'normal' | '13b' | 'eu_erwerb' | 'ohne';
export type Zahlweg = 'bank' | 'bar' | 'privat';

export interface Kategorie {
  id: string;
  richtung: 'ausgabe' | 'einnahme';
  name: string;
  euer: string;
  satz: Satz;
  ohneSteuer?: boolean;
}

// Lieferant aus der Lieferantenliste (Migration 0080), mit seinem heutigen Namen
export interface LieferantKurz {
  id: string;
  nummer: number;
  name: string;
}

export interface Ausgabe {
  id: string;
  nummer: string;
  richtung: 'ausgabe' | 'einnahme';
  belegdatum: string;
  bezahltAm: string | null;
  zahlweg: Zahlweg;
  partner: string;
  partnerUstid: string | null;
  belegnummer: string | null;
  text: string | null;
  kategorie: string;
  steuerart: Steuerart;
  zeilen: Zeile[];
  netto: number;
  steuer: number;
  brutto: number;
  nutzungsdauer: number | null;
  beleg: { id: string; name: string; mime: string } | null;
  // Verweis auf die Lieferantenliste; partner bleibt die Abschrift vom Beleg
  lieferant: LieferantKurz | null;
  erechnung: unknown;
  storniert: { am: string; grund: string } | null;
}

export interface AusgabenListe {
  jahr: number;
  heute: string;
  kleinunternehmer: boolean;
  kategorien: Kategorie[];
  ausgaben: Ausgabe[];
}

// Ausgaben eines Lieferanten (Karte am Lieferanten): alle Jahre, neueste zuerst; mehr: es gibt mehr als die Liste
export interface LieferantAusgaben {
  jahr: number | null;
  heute: string;
  kleinunternehmer: boolean;
  kategorien: Kategorie[];
  lieferant: LieferantKurz;
  mehr: boolean;
  ausgaben: Ausgabe[];
}

export interface Vorschlag {
  partner: string | null;
  partnerUstid: string | null;
  belegnummer: string | null;
  belegdatum: string | null;
  steuerart: Steuerart;
  zeilen: Zeile[];
  hinweise: string[];
  // passender Lieferant aus der Liste (gleiche USt-IdNr., sonst gleicher Name), nur als Vorschlag
  lieferantId: string | null;
}
export interface BelegAntwort {
  beleg: { id: string; name: string; mime: string };
  erechnung: unknown;
  vorschlag: Vorschlag | null;
}

export type KasseArt = 'anfang' | 'einnahme' | 'ausgabe' | 'einlage' | 'entnahme' | 'bank_ein' | 'bank_aus' | 'storno';
export interface KasseBuchung {
  id: string;
  nr: number;
  datum: string;
  art: KasseArt;
  artText: string;
  betrag: number;
  satz: number | null;
  text: string;
  beleg: string | null;
  ausgabeId: string | null;
  bestand: number;
  storniert: boolean;
  stornoVon: string | null;
}
export interface Kasse {
  jahr: number;
  heute: string;
  bestand: number;
  letztesDatum: string | null;
  anfangJahr: number;
  leer: boolean;
  buchungen: KasseBuchung[];
}

export interface SteuerEinstellung {
  versteuerung: 'soll' | 'ist';
  zeitraum: 'monat' | 'quartal';
  dauerfrist: boolean;
  bundesland: string | null;
  euUmsaetze: 'leistungen' | 'waren';
  gespeichert: boolean;
}
export interface SteuerSeite extends SteuerEinstellung {
  steuernummer: string | null;
  steuernummerElster: string | null;
  steuernummerPasst: boolean | null;
  kleinunternehmer: boolean;
  bundeslaender: { id: string; name: string; muster: string }[];
  elster: { bereit: boolean; grund: string | null; test: boolean };
  auftrag: ElsterAuftrag;
  inhaber: boolean;
}

// „Billtoro sendet für Sie“: möglich = Billtoro kann gerade senden; erteilt = die Firma hat zugestimmt
export interface ElsterAuftrag {
  moeglich: boolean;
  text: string;
  erteilt: { name: string; am: string } | null;
}

export interface Abgabe {
  id: string;
  jahr: number;
  zeitraum: number;
  art: 'elster' | 'test' | 'hand';
  berichtigt: boolean;
  kennzahlen: Record<string, number>;
  zahllast: number;
  ticket: string | null;
  gesendetAm: string;
  protokoll: boolean;
}
export interface UstvaZeitraum {
  zeitraum: number;
  name: string;
  von: string;
  bis: string;
  frist: string;
  laeuft: boolean;
  zahllast: number;
  abgegeben: { art: Abgabe['art']; am: string; zahllast: number } | null;
  geaendert: boolean;
}
export interface UstvaListe {
  jahr: number;
  heute: string;
  einstellung: SteuerEinstellung;
  zeitraeume: UstvaZeitraum[];
  abgaben: Abgabe[];
}
export interface UstvaDetail {
  periode: { jahr: number; zeitraum: number; name: string; von: string; bis: string };
  versteuerung: 'soll' | 'ist';
  klein: boolean;
  kennzahlen: Record<string, number>;
  steuer: Record<string, number>;
  umsatzsteuerCents: number;
  vorsteuerCents: number;
  zahllastCents: number;
  hinweise: string[];
  anzahl: { umsaetze: number; ausgaben: number };
  frist: string;
  kzText: Record<string, string>;
  kzEuro: string[];
  abgaben: Abgabe[];
  geaendert: boolean;
  sondervorauszahlung: boolean;
  steuernummer: string | null;
  steuernummerElster: string | null;
  elster: { bereit: boolean; grund: string | null };
  auftrag: ElsterAuftrag;
  inhaber: boolean;
}

export interface EuerZeile {
  gruppe: 'einnahmen' | 'ausgaben';
  name: string;
  cents: number;
}
export interface Euer {
  jahr: number;
  einnahmen: EuerZeile[];
  ausgaben: EuerZeile[];
  summeEinnahmen: number;
  summeAusgaben: number;
  gewinn: number;
  nichtAbziehbar: number;
  anlagen: { nummer: string; text: string; nettoCents: number; afaCents: number; restCents: number }[];
}

export const basis = (firmId: string) => `/api/rechnungstool/${firmId}`;
export const ausgabenVonLieferant = (firmId: string, id: string) => `${basis(firmId)}/ausgaben?lieferant=${encodeURIComponent(id)}`;

// Lieferanten-Angaben aus der gespeicherten bzw. gelesenen E-Rechnung (vom Server, hier ohne festen Typ)
export function erechnungLieferant(e: unknown): { name: string | null; ustId: string | null; land: string | null; iban: string | null } | null {
  const l = (e as { lieferant?: Record<string, unknown> } | null)?.lieferant;
  if (!l) return null;
  const s = (k: string) => (typeof l[k] === 'string' && l[k] ? (l[k] as string) : null);
  return { name: s('name'), ustId: s('ustId'), land: s('land'), iban: s('iban') };
}

// Land für einen neuen Lieferanten: aus der USt-IdNr. (muss zum Land passen, der Server prüft das), sonst aus der
// E-Rechnung, sonst Deutschland
export function landFuer(land: string | null, ustId: string | null): string {
  const u = (ustId ?? '').replace(/\s/g, '').toUpperCase();
  if (u.startsWith('DE')) return 'DE';
  const eu = EU_LAENDER.find((x) => u.startsWith(x[2]));
  if (eu) return eu[0];
  const l = land?.toUpperCase() ?? '';
  return l === 'DE' || EU_LAENDER.some((x) => x[0] === l) || istDrittland(l) ? l : 'DE';
}

export const euro = (cents: number) =>
  `${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
export const euroGanz = (e: number) => `${e.toLocaleString('de-DE')} €`;
export const deDatum = (iso: string | null | undefined) => (iso ? iso.split('-').reverse().join('.') : '');
export const centsText = (cents: number) => (cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: false });

// „12,50“, „1.234,5“ oder „-3“ → Cent; undefined bei falscher Eingabe, null bei leer
export function centsAus(text: string): number | null | undefined {
  const t = text.trim().replace(/\s|€/g, '');
  if (!t) return null;
  if (!/^-?(\d{1,3}(\.\d{3})+|\d+)(,\d{1,2})?$/.test(t)) return undefined;
  const n = Number(t.replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : undefined;
}

// Steuer aus Brutto (kaufmännisch gerundet, wie der Server)
const runden = (x: number) => Math.sign(x) * Math.round(Math.abs(x)) + 0;
export function ausBrutto(bruttoCents: number, satz: number): Zeile {
  const steuerCents = runden((bruttoCents * satz) / (100 + satz));
  return { satz: satz as Satz, nettoCents: bruttoCents - steuerCents, steuerCents };
}

export function speichernAls(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export async function herunterladen(url: string, name: string) {
  speichernAls(await api.file(url), name);
}

export async function oeffnen(url: string) {
  const blob = await api.file(url);
  const u = URL.createObjectURL(blob);
  window.open(u, '_blank', 'noopener');
  window.setTimeout(() => URL.revokeObjectURL(u), 60_000);
}

export const ZAHLWEG: [Zahlweg, string][] = [
  ['bank', 'Bank / Karte'],
  ['bar', 'Bar (Kasse)'],
  ['privat', 'Aus eigener Tasche'],
];
export const STEUERART: [Steuerart, string][] = [
  ['normal', 'Normal mit Umsatzsteuer'],
  ['13b', 'Ich schulde die Steuer (z. B. Firma aus dem Ausland, Bauleistung)'],
  ['eu_erwerb', 'Ware aus einem anderen EU-Land'],
  ['ohne', 'Ohne Umsatzsteuer'],
];
